import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigProvider } from '@app/app.config.provider';
import { IMarketRate, IMarketRateForecastLocalStorage, IMarketRateForecastRequest } from '@models/beta/marketrate';
import { LocalStorageKeys } from '@models/store/local-storage-keys';
import { StoreActions } from '@models/store/store-actions';
import { IStoreState } from '@models/store/store-state';
import { LocalStorageService } from '@services/local-storage.service';
import { ObservableStore } from '@services/observable-store/dist';
import { getDateString } from '@shared/dates';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class MarketRateService extends ObservableStore<IStoreState> {
  private marketRateApi: string;
  private marketRateForecastApi: string;

  constructor(
    private readonly http: HttpClient,
    private readonly localStorageService: LocalStorageService,
    private readonly appConfig: AppConfigProvider
  ) {
    super({ trackStateHistory: true, logStateChanges: false });

    this.appConfig.environment.subscribe(env => {
      this.marketRateApi = `${env.API.REFERENCE_DATA}/api/marketrates`;
      this.marketRateForecastApi = `${env.API.REFERENCE_DATA}/api/marketrateforecasts`;
    });

    this.getMarketRates();
  }

  // Market Rates

  public getMarketRates(): Observable<IMarketRate[]> {
    const state = this.getState();
    if (state && state.marketRates) {
      return of(state.marketRates);
    } else {
      return this.fetchMarketRates();
    }
  }

  public fetchMarketRates(): Observable<IMarketRate[]> {
    return this.http.get<IMarketRate[]>(this.marketRateApi).pipe(
      map((marketRates: IMarketRate[]) => {
        marketRates = this.parseMarketRatesOnFetch(marketRates);
        marketRates = marketRates.filter(mr => mr.TimeSeries !== null);

        this.setState({ marketRates }, StoreActions.GetMarketRates);

        return marketRates;
      })
    );
  }

  public parseMarketRatesOnFetch(marketRates: IMarketRate[]) {
    // Transform them
    marketRates.forEach(mr => {
      if (mr.Forecasts && mr.Forecasts.length > 0) {
        mr.Forecasts.forEach(forecast => {
          // -- Parse PeriodEndDate
          forecast.PeriodEndDate = getDateString(forecast.PeriodEndDate);
        });
      }
    });

    return marketRates;
  }

  // Market Rate Forecasts DB
  public saveMarketRateForecasts(forecasts: IMarketRateForecastRequest[]): Observable<IMarketRateForecastRequest[]> {
    return this.http
      .post<IMarketRateForecastRequest[]>(this.marketRateForecastApi, forecasts)
      .pipe(tap(_ => this.fetchMarketRates().toPromise()));
  }

  // Local Storage

  public loadLocalStorageData() {
    const storageDb = this.localStorageService.fetchFromLocalStorage<IMarketRateForecastLocalStorage>(
      LocalStorageKeys.MARKET_RATE_FORECAST
    );
    if (storageDb && storageDb.payload) {
      return of(storageDb.payload);
    }

    return of(null);
  }

  public storeInLocalStorage(localStorageOBJ: IMarketRateForecastLocalStorage): void {
    if (localStorageOBJ) {
      this.localStorageService.storeInLocalStorage<IMarketRateForecastLocalStorage>(localStorageOBJ, LocalStorageKeys.MARKET_RATE_FORECAST);
    }
  }

  public discardLocalStorage(): void {
    this.localStorageService.clearFromLocalStorage(LocalStorageKeys.MARKET_RATE_FORECAST);
  }
}
